import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../../../services/api';
import InventoryStylePagination from '../../../components/InventoryStylePagination';
import { FiSearch, FiFilter, FiRefreshCw, FiEdit, FiUserPlus, FiEye } from 'react-icons/fi';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import PermissionButton from '../../../components/PermissionButton';
import { PermissionGate } from '../../../contexts/PermissionContext';
import { useNavigate, Link } from 'react-router-dom';

const UsersList = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    status: ''
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '', // Will be set dynamically based on available roles
    isActive: true
  });
  const navigate = useNavigate();

  // Fetch users with filters
  const { data, isLoading, isError, error, refetch } = useQuery(
    ['admin-users', page, limit, searchTerm, filters],
    () => adminAPI.getUsers({
      page,
      limit,
      search: searchTerm,
      ...filters
    }),
    {
      keepPreviousData: true
    }
  );

  // Fetch available roles
  const { data: rolesData } = useQuery(
    'admin-roles',
    () => adminAPI.getRoles(),
    {
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  );

  // Create user mutation
  const createUserMutation = useMutation(
    (userData) => adminAPI.createUser({
      ...userData,
      userType: 'internal' // Always internal for admin-created users
    }),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('admin-users');
        setIsCreateModalOpen(false);
        setFormData({
          name: '',
          email: '',
          role: getDefaultRole(),
          isActive: true
        });
      },
      onError: (error) => {
        console.error('Error creating user:', error);
        alert(error.response?.data?.message || 'Failed to create user');
      }
    }
  );

  // Update user mutation
  const updateUserMutation = useMutation(
    ({ id, data }) => adminAPI.updateUser(id, {
      ...data,
      userType: 'internal' // Always internal for admin-managed users
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-users');
        setIsEditModalOpen(false);
        setSelectedUser(null);
      }
    }
  );

  // Toggle user status mutation
  const toggleStatusMutation = useMutation(
    ({ userId, activate }) => 
      activate ? adminAPI.activateUser(userId) : adminAPI.deactivateUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-users');
      }
    }
  );

  // Resend welcome email mutation
  const resendWelcomeEmailMutation = useMutation(
    (userId) => adminAPI.resendWelcomeEmail(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-users');
        alert('Welcome email resent successfully!');
      },
      onError: (error) => {
        console.error('Error resending welcome email:', error);
        const errorMessage = error.response?.data?.message || 'Failed to resend welcome email';
        alert(`Error: ${errorMessage}`);
      }
    }
  );

  // Handle role filter change
  const handleRoleFilterChange = (e) => {
    setFilters({
      ...filters,
      role: e.target.value
    });
    setPage(1);
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setFilters({
      ...filters,
      status: e.target.value
    });
    setPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      role: '',
      status: ''
    });
    setSearchTerm('');
    setPage(1);
  };

  // Handle create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    createUserMutation.mutate(formData);
  };

  // Handle edit user
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    const updateData = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      ...(formData.password && { password: formData.password })
    };
    
    updateUserMutation.mutate({
      id: selectedUser.id,
      data: updateData
    });
  };

  // Handle toggle user status
  const handleToggleStatus = (user) => {
    toggleStatusMutation.mutate({
      userId: user.id,
      activate: !user.isActive
    });
  };

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.roles?.[0]?.name || getDefaultRole(), // Use default role if user has no role
      isActive: user.isActive
    });
    setIsEditModalOpen(true);
  };

  // Helper function to get user's primary role
  const getUserRole = (user) => {
    return user.roles?.[0]?.name || user.userType || 'regular_user';
  };

  // Helper function to format role display name
  const formatRoleName = (role) => {
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Helper function to check if user needs activation
  const userNeedsActivation = (user) => {
    // Check if user has a password reset token and password starts with temp_password_
    // Also check if the reset token hasn't expired
    return user.password_reset_token && 
           user.password?.startsWith('temp_password_') &&
           user.password_reset_expires && 
           new Date(user.password_reset_expires) > new Date();
  };

  const users = data?.data || [];
  const roles = rolesData?.data || [];
  
  // Filter out customer roles for internal user management
  const internalRoles = roles.filter(role => 
    !['customer_admin', 'regular_user'].includes(role.name)
  );

  // Get the default role (first internal role, or fallback)
  const getDefaultRole = () => {
    return internalRoles.length > 0 ? internalRoles[0].name : 'system_admin';
  };

  // Update form data when roles are loaded
  useEffect(() => {
    if (internalRoles.length > 0 && !formData.role) {
      setFormData(prev => ({
        ...prev,
        role: getDefaultRole()
      }));
    }
  }, [internalRoles.length]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error?.message || 'Error loading users'} />;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Internal Users</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage internal staff users and their role permissions.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <PermissionButton
            permissions="manage_users"
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
          >
            <FiUserPlus className="mr-2" />
            New User
          </PermissionButton>
        </div>
      </div>

      {/* Search and filters */}
      <div className="mt-6 mb-8 md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <form onSubmit={handleSearch} className="relative rounded-md shadow-sm md:w-96">
            <input
              type="text"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
          </form>
        </div>
        
        <div className="mt-4 md:mt-0 md:flex md:items-center md:space-x-4">
          <div className="flex items-center mb-2 md:mb-0">
            <FiFilter className="mr-2 text-gray-500" />
            <label htmlFor="role-filter" className="sr-only">Filter by role</label>
            <select
              id="role-filter"
              value={filters.role}
              onChange={handleRoleFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm"
            >
              <option value="">All Roles</option>
              {internalRoles.map(role => (
                <option key={role.id} value={role.name}>
                  {formatRoleName(role.name)}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center mb-2 md:mb-0">
            <label htmlFor="status-filter" className="sr-only">Filter by status</label>
            <select
              id="status-filter"
              value={filters.status}
              onChange={handleStatusFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Clear
            </button>
            
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FiRefreshCw className="mr-1 h-3 w-3" /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mt-8">
      {users && users.length > 0 ? (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block mt-8 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6">
                          Name
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                          Email
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                          Role
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                          Status
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                          Activation
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {users.map((user) => {
                        const userRole = getUserRole(user);
                        return (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              {user.name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {user.email}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                userRole === 'system_admin' ? 'bg-purple-100 text-purple-800' :
                                userRole === 'manager' ? 'bg-blue-100 text-blue-800' :
                                userRole === 'warehouse' ? 'bg-orange-100 text-orange-800' :
                                userRole === 'accounting' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {formatRoleName(userRole)}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {user.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              {userNeedsActivation(user) ? (
                                <div className="flex flex-col space-y-1">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Pending Activation
                                  </span>
                                  <PermissionGate permissions="manage_users">
                                    <button
                                      onClick={() => resendWelcomeEmailMutation.mutate(user.id)}
                                      disabled={resendWelcomeEmailMutation.isLoading}
                                      className="text-xs text-indigo-600 hover:text-indigo-900 disabled:text-gray-400"
                                    >
                                      {resendWelcomeEmailMutation.isLoading ? 'Sending...' : 'Resend Email'}
                                    </button>
                                  </PermissionGate>
                                </div>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Activated
                                </span>
                              )}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <div className="flex items-center justify-end space-x-3">
                                <PermissionGate permissions="view_users">
                                  <Link
                                    to={`/admin/users/${user.id}`}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    <FiEye className="h-5 w-5" />
                                    <span className="sr-only">View</span>
                                  </Link>
                                </PermissionGate>
                                
                                <PermissionGate permissions="manage_users">
                                  <Link
                                    to={`/admin/users/${user.id}/edit`}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    <FiEdit className="h-5 w-5" />
                                    <span className="sr-only">Edit</span>
                                  </Link>
                                </PermissionGate>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden">
            <div className="overflow-hidden">
              <div className="divide-y divide-gray-200">
                {users.map((user) => {
                  const userRole = getUserRole(user);
                  return (
                    <div key={user.id} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <PermissionGate permissions="view_users">
                            <Link
                              to={`/admin/users/${user.id}`}
                              className="text-indigo-600 hover:text-indigo-900 p-1"
                            >
                              <FiEye className="h-5 w-5" />
                            </Link>
                          </PermissionGate>
                          
                          <PermissionGate permissions="manage_users">
                            <Link
                              to={`/admin/users/${user.id}/edit`}
                              className="text-blue-600 hover:text-blue-900 p-1"
                            >
                              <FiEdit className="h-5 w-5" />
                            </Link>
                          </PermissionGate>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Role:</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            userRole === 'system_admin' ? 'bg-purple-100 text-purple-800' :
                            userRole === 'manager' ? 'bg-blue-100 text-blue-800' :
                            userRole === 'warehouse' ? 'bg-orange-100 text-orange-800' :
                            userRole === 'accounting' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {formatRoleName(userRole)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Activation:</span>
                          <div className="flex flex-col items-end space-y-1">
                            {userNeedsActivation(user) ? (
                              <>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Pending Activation
                                </span>
                                <PermissionGate permissions="manage_users">
                                  <button
                                    onClick={() => resendWelcomeEmailMutation.mutate(user.id)}
                                    disabled={resendWelcomeEmailMutation.isLoading}
                                    className="text-xs text-indigo-600 hover:text-indigo-900 disabled:text-gray-400"
                                  >
                                    {resendWelcomeEmailMutation.isLoading ? 'Sending...' : 'Resend Email'}
                                  </button>
                                </PermissionGate>
                              </>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Activated
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500">No users found.</div>
        </div>
      )}

        <InventoryStylePagination
          currentPage={page}
          totalPages={data?.totalPages || 1}
          totalItems={data?.total || 0}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />
      </div>

      {/* Create User Modal */}
      {isCreateModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsCreateModalOpen(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleCreateUser}>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Create New Internal User</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    The user will receive an email with instructions to set their password.
                  </p>
                  <div className="mt-4">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
                        <select
                          id="role"
                          name="role"
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          {internalRoles.map(role => (
                            <option key={role.id} value={role.name}>
                              {formatRoleName(role.name)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                    disabled={createUserMutation.isLoading}
                  >
                    {createUserMutation.isLoading ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleEditUser}>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Edit User</h3>
                  <div className="mt-2">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">Name</label>
                        <input
                          type="text"
                          name="edit-name"
                          id="edit-name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          name="edit-email"
                          id="edit-email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700">
                          New Password (leave blank to keep current)
                        </label>
                        <input
                          type="password"
                          name="edit-password"
                          id="edit-password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700">Role</label>
                        <select
                          id="edit-role"
                          name="edit-role"
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          {internalRoles.map(role => (
                            <option key={role.id} value={role.name}>
                              {formatRoleName(role.name)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                    disabled={updateUserMutation.isLoading}
                  >
                    {updateUserMutation.isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedUser(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList; 
